import { useTranslation } from 'react-i18next';
import { FunctionComponent, useState } from 'react';
import { Button, Card, Flex, GetProp, Table, TableProps, Upload, UploadProps } from 'antd';

import TableTotal from 'components/tableTotal/tableTotal';

import NotificationService from 'core/services/notification';

import { onGetRaiseCapital } from '../services/raiseCapitals';
import { PrivateEquityIssuance, PrivateEquityShareholder } from '../models/privateEquityIssuance';
import PrivateEquityShareholderModal from './PrivateEquityShareholderModal';
import Icon from 'utils/icon';
import { isRTL } from 'core/services/language';
import StatusComponent from 'components/status/status';
import ConfirmModalComponent from 'components/confirmModal/confirmModal.component';
import { onGetOwnersRegistryTemplate } from '../services/privateEquityIssuance';
import { downloadFile } from 'utils/downloadFile';
import Dragger from 'antd/es/upload/Dragger';
import newDocImage from 'assets/icons/newDoc.png';
import uploadedDocImage from 'assets/icons/uploadedDoc.png';
import ListButton from 'components/listButton/listButton';

// hooks
import useCompanySuspended from 'core/hooks/useCompanySuspended';

interface PrivateEquityIssuanceShareholdersTableProps {
	privateEquityIssuance: PrivateEquityIssuance;
	viewOnly?: boolean;
	fileUploaded?: boolean;
	totalNewShares?: number;
	onUpdateShareholders?: (shareholders: PrivateEquityShareholder[]) => void;
}

type FileType = Parameters<GetProp<UploadProps, 'beforeUpload'>>[0];

const PrivateEquityIssuanceShareholdersTable: FunctionComponent<PrivateEquityIssuanceShareholdersTableProps> = ({
	privateEquityIssuance,
	totalNewShares,
	fileUploaded,
	viewOnly,
	onUpdateShareholders,
}) => {
	const { t } = useTranslation();

	const { suspendedProps } = useCompanySuspended();

	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [shareholders, setShareholders] = useState<PrivateEquityShareholder[]>(privateEquityIssuance?.owners_registry?.owners_registry || []);

	const [currentPage, setCurrentPage] = useState<number>(1);
	const [totalRecords] = useState<number>(0);

	const [isDownloadingTemplate, setIsDownloadingTemplate] = useState<boolean>(false);

	const [addModalOpen, setAddModalOpen] = useState(false);
	const [clearModalOpen, setClearModalOpen] = useState(false);

	const [tempShareholder, setTempShareholder] = useState<PrivateEquityShareholder>();
	const [deleteModalOpen, setDeleteModalOpen] = useState(false);

	const columns: TableProps<PrivateEquityShareholder>['columns'] = [
		{
			title: t('ACTIONS.SHAREHOLDER_FULL_NAME'),
			dataIndex: 'name',
			key: 'name',
		},
		{
			title: t('COMMON.NATIONAL_ID'),
			dataIndex: 'national_id',
			key: 'national_id',
		},

		{
			title: t('COMMON.PHONE_NUMBER'),
			dataIndex: 'phone',
			key: 'phone',
			render: (_, record) => record.phone_country_code + record.phone_number,
		},
		{
			title: t('ACTIONS.OWNED_SHARES'),
			dataIndex: 'owned_shares',
			key: 'owned_shares',
			render: (_, record) => record.owned_shares.toLocaleString(),
		},
	];

	if (!viewOnly)
		columns.push({
			title: t('COMMON.ACTIONS'),
			dataIndex: 'actions',
			key: 'actions',
			render: (_, record) => (
				<Flex gap={10} align="center" justify="center">
					<ListButton
						type="edit"
						{...suspendedProps}
						text={t('COMMON.EDIT')}
						onClick={() => {
							setTempShareholder(record);
							setAddModalOpen(true);
						}}
					/>
					<ListButton
						type="delete"
						{...suspendedProps}
						text={t('COMMON.DELETE')}
						onClick={() => {
							setTempShareholder(record);
							setDeleteModalOpen(true);
						}}
					/>
				</Flex>
			),
		});

	const addShareholderHandler = (shareholder: PrivateEquityShareholder) => {
		if (shareholders.some((sh) => sh.national_id === shareholder.national_id)) {
			const updatedShareholders = shareholders.map((sh) => (sh.national_id === shareholder.national_id ? shareholder : sh));
			setShareholders(updatedShareholders);
			onUpdateShareholders?.(updatedShareholders);
		} else {
			setShareholders([...shareholders, shareholder]);
			onUpdateShareholders?.([...shareholders, shareholder]);
		}
		setAddModalOpen(false);
	};

	const downloadFileHandler = async () => {
		try {
			if (isDownloadingTemplate) return;

			setIsDownloadingTemplate(true);
			const response = await onGetOwnersRegistryTemplate();

			downloadFile(response.excel_file_url, `owners-registry-template.xlsx`);

			setIsDownloadingTemplate(false);
		} catch (error) {
			setIsDownloadingTemplate(false);
			NotificationService.error(t('COMMON.ERROR'), t('COMMON.FETCH_ERROR'));
		}
	};

	const fetchMoreData = async (page: number) => {
		try {
			setIsLoading(true);

			const response = await onGetRaiseCapital(privateEquityIssuance.action.id, page);
			setShareholders((response as PrivateEquityIssuance).owners_registry?.owners_registry || []);

			setCurrentPage(page);
			setIsLoading(false);
		} catch (error) {
			NotificationService.error(t('COMMON.ERROR'), t('COMMON.FETCH_ERROR'));
			setIsLoading(false);
		}
	};

	const beforeUploadHandler = (file: FileType) => {
		const isXlsx = file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' || file.name.endsWith('.xlsx');

		if (!isXlsx) {
			NotificationService.error(t('COMMON.ERROR'), t('COMMON.ONLY_XLSX'));
			return Upload.LIST_IGNORE;
		}

		const reader = new FileReader();

		reader.onload = (e) => {
			const data = e.target?.result;

			const XLSX = require('xlsx');

			const workbook = XLSX.read(data, { type: 'array' });

			const sheetName = workbook.SheetNames[0];
			const worksheet = workbook.Sheets[sheetName];

			const jsonData = XLSX.utils.sheet_to_json(worksheet);

			console.log({ jsonData });

			// check total shares counted in the file
			let totalSharesInFile = 0;
			for (const row of jsonData) {
				const ownedShares = Number(row['Owned Shares'] || 0);
				totalSharesInFile += ownedShares;
			}

			if (totalSharesInFile > (totalNewShares || 0)) {
				NotificationService.error(t('COMMON.ERROR'), t('ACTIONS.OWNED_SHARES_EXCEED_LIMIT', { limit: totalNewShares || 0 }));
				return;
			}

			// --- Map Excel columns to your model ---
			const mappedShareholders: PrivateEquityShareholder[] = jsonData.map((row: any) => ({
				name: row['Name'],
				national_id: row['National ID'],
				phone_country_code: String(row['Phone Country Code']),
				phone_number: String(row['Phone Number']),
				owned_shares: Number(row['Owned Shares']),
			}));

			setShareholders(mappedShareholders);
			onUpdateShareholders?.(mappedShareholders);
		};

		reader.readAsArrayBuffer(file);

		return Upload.LIST_IGNORE;
	};

	const deleteConfirmHandler = () => {
		setShareholders(shareholders.filter((sh) => sh.national_id !== tempShareholder?.national_id));
		setDeleteModalOpen(false);
	};

	return (
		<>
			{!viewOnly && (
				<div className="mb-4">
					<Icon name="registrationStep1" width={280} height={80} style={{ transform: isRTL() ? 'rotateY(180deg)' : 'unset' }} />

					<h6 className="fw-medium">{t('ACTIONS.ADD_OWNERS_REGISTRY')}</h6>
					<p>{t('ACTIONS.ADD_OWNERS_REGISTRY_DESCRIPTION')}</p>

					<Button size="middle" type="primary" loading={isDownloadingTemplate} onClick={downloadFileHandler}>
						{t('ACTIONS.DOWNLOAD_TEMPLATE')}
					</Button>
				</div>
			)}

			<Card className="mt-4">
				<Flex justify="space-between" align="center" className="mb-3">
					<StatusComponent color="green" text={`${t('ACTIONS.OWNERS')}: ${shareholders.length}`} />

					{!viewOnly && (
						<Flex gap={10} align="center">
							<Button
								size="middle"
								color="blue"
								style={{ background: '#cfeaff' }}
								variant="filled"
								onClick={() => setAddModalOpen(true)}
								disabled={totalNewShares! - shareholders.reduce((acc, shareholder) => acc + shareholder.owned_shares, 0) === 0}>
								<Icon
									name="plus"
									color={totalNewShares! - shareholders.reduce((acc, shareholder) => acc + shareholder.owned_shares, 0) === 0 ? '#ccc' : '#3a8dff'}
									width={15}
									height={15}
								/>
								{t('ACTIONS.ADD_NEW')}
							</Button>

							<Button
								size="middle"
								color="blue"
								style={{ background: '#cfeaff' }}
								variant="filled"
								disabled={shareholders.length === 0}
								onClick={() => setClearModalOpen(true)}>
								{t('ACTIONS.CLEAR_ALL_BOARD')}
							</Button>
						</Flex>
					)}
				</Flex>

				<Table
					columns={columns}
					dataSource={shareholders}
					scroll={{ x: 600 }}
					rowKey="national_id"
					loading={isLoading}
					locale={{
						emptyText: (
							<Dragger
								style={{ border: 'none', background: 'transparent' }}
								accept={'.xlsx'}
								showUploadList={false}
								beforeUpload={beforeUploadHandler}>
								<Flex
									justify="center"
									align="center"
									vertical
									gap={10}
									style={{
										background: '#fff',
										borderRadius: '10px',
										width: 'max-content',
										padding: 10,
										margin: 'auto',
										border: `2px solid ${fileUploaded ? '#4cb050' : 'rgba(86, 148, 242, 0.4)'}`,
									}}>
									<img src={fileUploaded ? uploadedDocImage : newDocImage} width={60} height={60} />
									<p className="fw-medium mb-0 text-center" style={{ color: fileUploaded ? '#4cb050' : 'rgba(86, 148, 242, 1)', width: 150 }}>
										{fileUploaded ? t('ACTIONS.FILE_UPLOADED') : t('ACTIONS.CLICK_OR_DRAG')}
									</p>
								</Flex>
							</Dragger>
						),
					}}
					pagination={{
						pageSize: 15,
						total: totalRecords,
						showSizeChanger: false,
						current: currentPage,
						onChange: fetchMoreData,
						showTotal: (total) => <TableTotal total={total} />,
					}}
				/>
			</Card>

			{!fileUploaded && (
				<StatusComponent
					className="mt-3 d-inline-block"
					bordered
					color={
						viewOnly || shareholders.reduce((acc, shareholder) => acc + Number(shareholder.owned_shares), 0) == Number(totalNewShares)
							? 'green'
							: 'red'
					}
					text={`${t('ACTIONS.TOTAL_NO_OF_SHARES')} ${shareholders.reduce((acc, shareholder) => acc + Number(shareholder.owned_shares), 0)} : ${
						totalNewShares || privateEquityIssuance?.action?.number_of_issued_units
					} ${t('COMMON.SHARES')}`}
				/>
			)}

			<PrivateEquityShareholderModal
				open={addModalOpen}
				onCancel={() => setAddModalOpen(false)}
				onConfirm={addShareholderHandler}
				shareholders={shareholders}
				shareholderData={tempShareholder}
				remainingShares={totalNewShares! - shareholders.reduce((acc, shareholder) => acc + shareholder.owned_shares, 0)}
			/>

			{/* CLEAR CONFIRMATION MODAL */}
			<ConfirmModalComponent
				confirmText={t('ACTIONS.CLEAR_ALL')}
				title={t('ACTIONS.CLEAR_ALL_BOARD')}
				open={clearModalOpen}
				onDiscard={() => setClearModalOpen(false)}
				onConfirm={() => {
					setShareholders([]);
					setClearModalOpen(false);
				}}
			/>

			{/* DELETE CONFIRMATION MODAL */}
			<ConfirmModalComponent
				confirmText={t('COMMON.CONFIRM')}
				confirmColor="red"
				title={t('ACTIONS.DELETE_SHAREHOLDER_CONFIRMATION')}
				open={deleteModalOpen}
				onDiscard={() => {
					setDeleteModalOpen(false);
					setTempShareholder(undefined);
				}}
				onConfirm={deleteConfirmHandler}
			/>
		</>
	);
};

export default PrivateEquityIssuanceShareholdersTable;
